import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { reportingAPI } from '../services/api';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, DollarSign, FileText, Users, Calendar, RefreshCw, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import DatePicker, { registerLocale } from "react-datepicker";
import fr from 'date-fns/locale/fr';
import "react-datepicker/dist/react-datepicker.css";

registerLocale('fr', fr);

const COLORS = ['#1a3a5c', '#c8a437', '#10b981', '#ef4444', '#8b5cf6'];

// Date presets for quick filtering
const PRESETS = [
    { label: 'Tout', value: 'all' },
    { label: 'Cette année', value: 'year' },
    { label: 'Ce trimestre', value: 'quarter' },
    { label: 'Ce mois', value: 'month' },
    { label: '30 derniers jours', value: '30d' },
    { label: '12 derniers mois', value: '12m' },
];

const getPresetDates = (preset) => {
    const now = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];

    switch (preset) {
        case 'month': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            return { dateFrom: fmt(start), dateTo: fmt(end) };
        }
        case 'quarter': {
            const q = Math.floor(now.getMonth() / 3);
            const start = new Date(now.getFullYear(), q * 3, 1);
            const end = new Date(now.getFullYear(), q * 3 + 3, 0);
            return { dateFrom: fmt(start), dateTo: fmt(end) };
        }
        case 'year': {
            return { dateFrom: `${now.getFullYear()}-01-01`, dateTo: `${now.getFullYear()}-12-31` };
        }
        case '30d': {
            const start = new Date(now); start.setDate(start.getDate() - 30);
            return { dateFrom: fmt(start), dateTo: fmt(now) };
        }
        case '12m': {
            const start = new Date(now); start.setMonth(start.getMonth() - 12);
            return { dateFrom: fmt(start), dateTo: fmt(now) };
        }
        default:
            return { dateFrom: '', dateTo: '' };
    }
};

const CustomYAxisTick = ({ x, y, payload, data }) => {
    const item = data.find(d => d.nom_assurance === payload.value);
    if (!item?.assurance_logo) {
        return (
            <text x={x - 8} y={y} dy={4} textAnchor="end" fill="#666" style={{ fontSize: '10px', fontWeight: '600' }}>
                {payload.value?.length > 15 ? payload.value.substring(0, 12) + '...' : payload.value}
            </text>
        );
    }

    return (
        <g transform={`translate(${x - 70},${y - 20})`}>
            <rect x="0" y="0" width="60" height="40" fill="white" rx="8" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.05))', stroke: '#f3f4f6', strokeWidth: 1 }} />
            <image
                href={`/assurances_logo/${item.assurance_logo}`}
                x="5"
                y="5"
                height="30"
                width="50"
                preserveAspectRatio="xMidYMid meet"
            />
        </g>
    );
};

const ReportingPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [preset, setPreset] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const res = await reportingAPI.get(params);
            setData(res.data.data);
        } catch {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => { loadData(); }, [loadData]);

    const applyPreset = (p) => {
        setPreset(p);
        const dates = getPresetDates(p);
        setDateFrom(dates.dateFrom);
        setDateTo(dates.dateTo);
    };

    const handleCustomDate = (field, value) => {
        setPreset('custom');
        if (field === 'from') setDateFrom(value);
        else setDateTo(value);
    };

    const formatCurrency = (v) => v ? new Intl.NumberFormat('fr-DZ').format(Math.round(v)) + ' DZD' : '0 DZD';

    const exportCSV = () => {
        if (!data) return;
        const rows = [
            ['Indicateur', 'Valeur'],
            ['Total Sinistres', data.statistiques?.total_sinistres || 0],
            ['Total Rapports', data.statistiques?.total_rapports || 0],
            ['Total Honoraires', data.statistiques?.total_honoraires || 0],
            ['CA Encaissé (DZD)', data.statistiques?.ca_total || 0],
        ];
        const csv = rows.map(r => r.join(';')).join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reporting_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Export CSV téléchargé');
    };

    if (loading) return <Layout title="Reporting"><div className="loading-spinner"><div className="spinner" /></div></Layout>;

    const stats = data?.statistiques || {};

    return (
        <Layout title="Reporting Global">
            <div>
                {/* Header + filters */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                        <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary-800)', marginBottom: '4px' }}>
                            📊 Reporting &amp; Statistiques Globales
                        </h2>
                        {(dateFrom || dateTo) && (
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                Période : {dateFrom || '—'} → {dateTo || '—'}
                            </p>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button className="btn btn-ghost btn-sm" onClick={loadData} title="Actualiser">
                            <RefreshCw size={14} />
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={exportCSV} title="Exporter CSV">
                            <Download size={14} /> CSV
                        </button>
                    </div>
                </div>

                {/* Date Filters */}
                <div className="card" style={{ padding: '16px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--primary-600)', flexShrink: 0 }}>
                            <Calendar size={15} />
                            <span style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Période</span>
                        </div>

                        {/* Quick presets */}
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {PRESETS.map(p => (
                                <button
                                    key={p.value}
                                    onClick={() => applyPreset(p.value)}
                                    style={{
                                        padding: '5px 12px', borderRadius: '20px', fontSize: '12px',
                                        fontWeight: '600', border: '1px solid',
                                        cursor: 'pointer', transition: 'all 0.15s',
                                        background: preset === p.value ? 'var(--primary-700)' : 'white',
                                        color: preset === p.value ? 'white' : 'var(--text-secondary)',
                                        borderColor: preset === p.value ? 'var(--primary-700)' : 'var(--border)',
                                    }}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        {/* Divider */}
                        <div style={{ width: '1px', height: '24px', background: 'var(--border)', flexShrink: 0 }} />

                        {/* Custom date range */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <DatePicker
                                selected={dateFrom ? new Date(dateFrom) : null}
                                onChange={(date) => handleCustomDate('from', date ? date.toISOString().split('T')[0] : '')}
                                className="form-control"
                                style={{ padding: '5px 10px', height: '34px', fontSize: '12px', width: '120px' }}
                                dateFormat="dd/MM/yyyy"
                                locale="fr"
                                placeholderText="jj/mm/aaaa"
                            />
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>→</span>
                            <DatePicker
                                selected={dateTo ? new Date(dateTo) : null}
                                onChange={(date) => handleCustomDate('to', date ? date.toISOString().split('T')[0] : '')}
                                className="form-control"
                                style={{ padding: '5px 10px', height: '34px', fontSize: '12px', width: '120px' }}
                                dateFormat="dd/MM/yyyy"
                                locale="fr"
                                placeholderText="jj/mm/aaaa"
                            />
                            {(dateFrom || dateTo) && (
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => { setPreset('all'); setDateFrom(''); setDateTo(''); }}
                                    style={{ fontSize: '11px', color: 'var(--danger)' }}
                                >
                                    ✕ Effacer
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* KPIs */}
                <div className="stats-grid" style={{ marginBottom: '24px' }}>
                    {[
                        { label: 'Total Sinistres', value: stats.total_sinistres || 0, icon: Users, color: 'blue', suffix: '' },
                        { label: 'Rapports PV', value: stats.total_rapports || 0, icon: FileText, color: 'gold', suffix: '' },
                        { label: 'CA Encaissé', value: formatCurrency(stats.ca_total), icon: DollarSign, color: 'green', suffix: '' },
                        { label: 'Honoraires Émis', value: stats.total_honoraires || 0, icon: TrendingUp, color: 'blue', suffix: '' },
                    ].map(item => {
                        const Icon = item.icon;
                        return (
                            <div key={item.label} className={`stat-card ${item.color}`}>
                                <div className={`stat-icon ${item.color}`}><Icon size={22} /></div>
                                <div className="stat-number" style={{ fontSize: typeof item.value === 'string' ? '16px' : '28px' }}>{item.value}</div>
                                <div className="stat-label">{item.label}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Charts - Row 1 */}
                <div className="grid-2" style={{ gap: '16px', marginBottom: '16px' }}>
                    {/* Sinistres par statut */}
                    <div className="card">
                        <div className="card-title" style={{ marginBottom: '16px' }}>Sinistres par Statut</div>
                        {data?.sinistres_par_statut?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={data?.sinistres_par_statut || []} dataKey="total" nameKey="statut" cx="50%" cy="50%" outerRadius={80} label={({ statut, total }) => `${statut}: ${total}`}>
                                        {Array.isArray(data?.sinistres_par_statut) && data.sinistres_par_statut.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: 'var(--shadow-lg)' }} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="empty-state"><p>Pas de données disponibles</p></div>
                        )}
                    </div>

                    {/* Top Assurances */}
                    <div className="card">
                        <div className="card-title" style={{ marginBottom: '16px' }}>Top Compagnies d'Assurance</div>
                        {data?.top_assurances?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart
                                    data={(Array.isArray(data?.top_assurances) ? data.top_assurances : []).slice(0, 8)}
                                    layout="vertical"
                                    margin={{ left: 60, right: 30, top: 10, bottom: 10 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                                    <YAxis
                                        dataKey="nom_assurance"
                                        type="category"
                                        width={100}
                                        tick={(props) => <CustomYAxisTick {...props} data={data.top_assurances} />}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        cursor={{ fill: '#f9fafb' }}
                                        contentStyle={{ borderRadius: '15px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', padding: '12px' }}
                                        labelStyle={{ fontWeight: '800', color: 'var(--primary-800)', marginBottom: '5px' }}
                                    />
                                    <Bar dataKey="total" name="Nombre de dossiers" fill="#1a3a5c" radius={[0, 10, 10, 0]} barSize={35} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="empty-state"><p>Pas de données disponibles</p></div>
                        )}
                    </div>
                </div>

                {/* Charts - Row 2 */}
                <div className="grid-2" style={{ gap: '16px' }}>
                    {/* Évolution mensuelle */}
                    <div className="card">
                        <div className="card-title" style={{ marginBottom: '16px' }}>Évolution Mensuelle (Sinistres)</div>
                        {data?.evolution_mensuelle?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={220}>
                                <LineChart data={data?.evolution_mensuelle || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                    <XAxis dataKey="mois" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: 'var(--shadow-lg)' }} />
                                    <Line type="monotone" dataKey="sinistres" stroke="#1a3a5c" strokeWidth={2.5} dot={{ fill: '#c8a437', r: 4 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="empty-state"><p>Pas de données disponibles</p></div>
                        )}
                    </div>

                    {/* Honoraires */}
                    <div className="card">
                        <div className="card-title" style={{ marginBottom: '16px' }}>Suivi des Honoraires</div>
                        <div style={{ display: 'grid', gap: '12px' }}>
                            {[
                                { label: 'Notes Payées', value: stats.honoraires_payes || 0, color: 'var(--success)', percent: stats.total_honoraires ? Math.round((stats.honoraires_payes / stats.total_honoraires) * 100) : 0 },
                                { label: 'Notes Émises', value: stats.honoraires_emis || 0, color: 'var(--primary-500)', percent: stats.total_honoraires ? Math.round((stats.honoraires_emis / stats.total_honoraires) * 100) : 0 },
                                { label: 'Brouillons', value: stats.honoraires_brouillon || 0, color: 'var(--text-muted)', percent: stats.total_honoraires ? Math.round((stats.honoraires_brouillon / stats.total_honoraires) * 100) : 0 },
                            ].map(item => (
                                <div key={item.label}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{item.label}</span>
                                        <span style={{ fontWeight: '700', fontSize: '13px' }}>{item.value} ({item.percent}%)</span>
                                    </div>
                                    <div style={{ background: 'var(--surface-3)', borderRadius: '10px', height: '8px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${item.percent}%`, background: item.color, borderRadius: '10px', transition: 'width 1s ease' }} />
                                    </div>
                                </div>
                            ))}

                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '4px' }}>
                                <div style={{ fontWeight: '700', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Chiffre d'Affaires</div>
                                <div style={{ fontSize: '24px', fontWeight: '900', color: 'var(--primary-700)' }}>{formatCurrency(stats.ca_total)}</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Total encaissé</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ReportingPage;
